import React, { FC, CSSProperties, memo } from 'react'
import styles from '../memory.module.css'
import { CardType } from '../../../types/memory'
import useWindowSize from '../../../hooks/useWindowSize'
import { useLanguageContext } from '../../../contexts/LanguageContext'

interface Card {
  id: number
  value: string | React.JSX.Element
}

interface GameGridProps {
  setGameStarted: (value: boolean) => void
  gridSize: number
  cards: Card[]
  flippedCards: number[]
  flippedOverCards: number[]
  matchedCards: number[]
  cardType: {
    value: string
  }
  handleCardClick: (index: number) => void
  renderCardContent: (card: Card) => Card['value']
}

const GameGrid: FC<GameGridProps> = ({
  setGameStarted,
  gridSize,
  cards,
  flippedCards,
  flippedOverCards,
  matchedCards,
  cardType,
  handleCardClick,
  renderCardContent,
}) => {
  const { t } = useLanguageContext()

  const { windowHeight, windowWidth } = useWindowSize()
  const size = `size${gridSize}`

  return (
    <>
      <div className={`${styles['grid-wrap']} ${styles[size]}`}>
        <div
          className={styles.grid}
          style={{
            gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
            gridTemplateRows: `repeat(${gridSize}, 1fr)`,
            ['--size' as string]: `${windowHeight > windowWidth ? 94 : 85}`,
            ['--amount' as string]: `${gridSize}`,
            ['--multiplier' as string]: `${
              windowHeight > windowWidth ? '1vw' : '1vh'
            }`,
          }}
        >
          {cards.map((card, index) => {
            const fontSize =
              cardType.value === (CardType.icons as string)
                ? windowHeight > windowWidth
                  ? `${50 / gridSize}vw`
                  : `${50 / gridSize}vh`
                : windowHeight > windowWidth
                  ? `${40 / gridSize}vw`
                  : `${40 / gridSize}vh`

            const cardStyle: CSSProperties = {
              ['--fontSize' as string]: fontSize,
            }

            return (
              <div
                key={card.id}
                style={cardStyle}
                className={`${styles.card} ${
                  flippedCards.includes(index) || matchedCards.includes(index)
                    ? styles.flipped
                    : ''
                } ${
                  flippedOverCards.includes(index) ? styles['flipped-over'] : ''
                }`}
                onClick={() => handleCardClick(index)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleCardClick(index)
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div className={styles.front}>{renderCardContent(card)}</div>
                <div className={styles.back}></div>
              </div>
            )
          })}
        </div>
      </div>
      <button
        className={styles.finish}
        type="button"
        onClick={() => {
          setGameStarted(false)
        }}
      >
        {t('StopGame')}
      </button>
    </>
  )
}

export default memo(GameGrid)
